/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { KEYCODE } from '../../utils/keycode.js';

export const TimeModifierMixin = (superClass) =>
  class extends superClass {
    incrementTime(keydownedInputBox) {
      //user might prefer to select am / pm by up/down arrows. So the condition of am/pm was excluded here
      this.previousHourValue = Number(this.h);
      if (this.h === '' || this.m === '' || (this.s === '' && !this.mini)) {
        this.updateWithSystemTime();
        return;
      }
      switch (keydownedInputBox.dataset.id) {
        case 's':
        case 's-ip':
          this.s = parseInt(this.s, 10) + parseInt(this.step, 10);
          if (this.s > 59) {
            this.m = parseInt(this.m, 10) + 1;
            this.s = this.s - 60;
          }
          if (this.s < 10) {
            this.s = `0${this.s}`;
          }
        case 'm':
        case 'm-ip':
          if (keydownedInputBox.dataset.id === 'm' || keydownedInputBox.dataset.id === 'm-ip') {
            this.m = parseInt(this.m, 10) + parseInt(this.step, 10);
          }
          if (this.m > 59) {
            this.h = parseInt(this.h, 10) + 1;
            this.m = this.m - 60;
          }
          if (Number(this.m) < 10) {
            this.m = `0${Number(this.m)}`;
          }
        case 'h':
        case 'h-ip':
          if (keydownedInputBox.dataset.id === 'h' || keydownedInputBox.dataset.id === 'h-ip') {
            this.h = parseInt(this.h, 10) + 1;
          }
          if (this.clockFormat === '12-hour') {
            if (this.h === 12 && this.previousHourValue === 11) {
              this.amPm = this.amPm === 'PM' ? 'AM' : 'PM';
            }
            if (this.h === 13 && this.previousHourValue === 12) {
              this.h = 1;
            }
          } else {
            if (this.h === 24 && this.previousHourValue === 23) {
              this.h = 0;
            }
          }
          if (Number(this.h) < 10) {
            this.h = `0${Number(this.h)}`;
          }

        default:
          //incrementAMPM
          if (keydownedInputBox.dataset.id === 'ap' || keydownedInputBox.dataset.id === 'ap-ip') {
            this.amPm = this.amPm === 'PM' ? 'AM' : 'PM';
          }
          break;
      }
      dispatchCustomEvent(this, 'time-value-changed');
    }
    decrementTime(keydownedInputBox) {
      this.previousHourValue = Number(this.h);

      if (this.h === '' || this.m === '' || (this.s === '' && !this.mini)) {
        this.updateWithSystemTime(this);
        return;
      }
      switch (keydownedInputBox.dataset.id) {
        case 's':
        case 's-ip':
          this.s = parseInt(this.s, 10) - parseInt(this.step, 10);
          let subvalue = this.s % this.step === 0 ? this.step : this.s % this.step;
          if (this.s < 0) {
            this.m = parseInt(this.m, 10) - 1;
            this.s = 60 - Math.abs(parseInt(subvalue));
          }
          if (this.s < 10) {
            this.s = `0${this.s}`;
          }
        case 'm':
        case 'm-ip':
          if (keydownedInputBox.dataset.id === 'm' || keydownedInputBox.dataset.id === 'm-ip') {
            this.m = parseInt(this.m, 10) - parseInt(this.step, 10);
          }
          let minsubVal = this.m % this.step === 0 ? this.step : this.m % this.step;

          if (this.m < 0) {
            this.h = parseInt(this.h, 10) - 1;
            this.m = 60 - Math.abs(parseInt(minsubVal));
          }
          if (Number(this.m) < 10) {
            this.m = `0${Number(this.m)}`;
          }
        case 'h':
        case 'h-ip':
          this.h = parseInt(this.h, 10);
          if (keydownedInputBox.dataset.id === 'h' || keydownedInputBox.dataset.id === 'h-ip') {
            this.h = parseInt(this.h, 10) - 1;
          }
          if (this.clockFormat === '12-hour') {
            if (this.h === 11 && this.previousHourValue === 12) {
              this.amPm = this.amPm === 'PM' ? 'AM' : 'PM';
            }
            if (
              (this.h === 0 && this.previousHourValue === 1) ||
              (this.h === -1 && this.previousHourValue === 0)
            ) {
              this.h = 12;
            }
          } else if (this.clockFormat === '24-hour') {
            if (this.h === -1 && this.previousHourValue === 0) {
              this.h = 23;
            }
          }
          if (Number(this.h) < 10) {
            this.h = `0${Number(this.h)}`;
          }
          break;
        default:
          if (keydownedInputBox.dataset.id === 'ap' || keydownedInputBox.dataset.id === 'ap-ip') {
            this.amPm = this.amPm === 'PM' ? 'AM' : 'PM';
          }
          break;
      }
      dispatchCustomEvent(this, 'time-value-changed');
    }
    updateWithSystemTime() {
      const presentBrowserTime = new Date();
      let hour = presentBrowserTime.getHours();
      /*** Initial display logic*/
      if (this.clockFormat === '12-hour') {
        this.amPm = (hour > 11 && hour !== 12) || hour === 12 ? 'PM' : hour === 0 ? 'AM' : 'AM';
        hour = hour > 11 && hour !== 12 ? hour - 12 : hour === 12 || hour === 0 ? 12 : hour;
      } else if (this.clockFormat === '24-hour') {
        this.amPm = '';
      }
      /**
       * If number is less than 10, then make it like '0x' , eg: '02' for 2
       */
      this.h = hour < 10 ? `0${hour}` : hour;
      this.m =
        presentBrowserTime.getMinutes() < 10
          ? `0${presentBrowserTime.getMinutes()}`
          : presentBrowserTime.getMinutes();
      this.s =
        presentBrowserTime.getSeconds() < 10
          ? `0${presentBrowserTime.getSeconds()}`
          : presentBrowserTime.getSeconds();
      dispatchCustomEvent(this, 'time-value-changed');
    }
    incrementOrDecrementOnKeyDown(e) {
      let eventSrc;
      if (e.composedPath()) {
        eventSrc = e.composedPath()[0];
      } else {
        eventSrc = e.srcElement;
      }
      if (e.keyCode === KEYCODE.ARROW_UP || e.key === 'Up') {
        e.preventDefault();
        this.incrementTime(eventSrc);
      }
      if (e.keyCode === KEYCODE.ARROW_DOWN || e.key === 'Down') {
        e.preventDefault();
        this.decrementTime(eventSrc);
      }
      dispatchCustomEvent(this, 'time-value-changed');
    }
  };
